import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useJobIntelProfile } from '@/hooks/useJobIntelProfile';
import { ManualProfileForm } from '@/components/JobIntelProfile/ManualProfileForm';
import { ProfileCompletionBadge } from '@/components/JobIntelProfile/ProfileCompletionBadge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Settings, ArrowLeft } from 'lucide-react';

export default function JobIntelProfileEdit() {
  const navigate = useNavigate();
  const { profile, loading, saveProfile, completionPercentage } = useJobIntelProfile();
  const [saved, setSaved] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center space-y-4">
            <Settings className="h-12 w-12 text-muted-foreground mx-auto" />
            <h2 className="text-xl font-bold">No Profile Yet</h2>
            <p className="text-muted-foreground">Set up your career profile to get started with JobIntel 360.</p>
            <Button onClick={() => navigate('/jobintel-profile-setup')} className="bg-[hsl(var(--primary))]">
              Set Up Profile
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (saved) {
    return (
      <div className="min-h-screen bg-background p-6 flex items-center justify-center">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center space-y-4">
            <ProfileCompletionBadge percentage={completionPercentage} size="lg" />
            <h2 className="text-xl font-bold text-foreground">Profile Updated!</h2>
            <Button variant="outline" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-3xl mx-auto mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">My Career Profile</h1>
            <p className="text-muted-foreground">Edit your professional qualifications and preferences</p>
          </div>
          <ProfileCompletionBadge percentage={completionPercentage} size="md" />
        </div>
      </div>

      <ManualProfileForm
        initialData={profile}
        onComplete={async (data) => {
          await saveProfile(data);
          setSaved(true);
        }}
        onBack={() => navigate(-1)}
      />
    </div>
  );
}
